import { FC, ReactNode, useEffect, useState } from 'react';
import classNames from 'classnames';

import dayjs from 'dayjs';

import ModalWrap from '../../common/modal-wrap';
import { CloseCrossBtn } from '../../icons/close-cross-btn';
import Input from '../../common/input';
import Button from '../../common/button';
import { CustomSelect } from '../../common/select';
import { DatePickerCustom } from '../../common/datepicker';

type RadioOptionProps = {
  name: string;
  checked: boolean;
  onChange: () => void;
  label: string;
  children?: ReactNode;
  labelWrap?: boolean;
};

const RadioOption: FC<RadioOptionProps> = ({
  name,
  checked,
  onChange,
  label,
  children,
  labelWrap = false,
}) => (
  <label
    className={classNames(
      'flex items-center gap-3 cursor-pointer',
      labelWrap && 'flex-wrap',
    )}
  >
    <input
      type="radio"
      name={name}
      checked={checked}
      onChange={onChange}
      className="sr-only"
    />
    <span
      className={classNames(
        'shrink-0 w-[20px] h-[20px] rounded-full bg-white transition-colors',
        checked ? 'border-[6px] border-[#C00000]' : 'border-2 border-[#d1d5db]',
      )}
    />
    <span className="font-manrope text-base text-[#2C3038]">{label}</span>
    {children}
  </label>
);

const INTERVAL_UNIT_OPTIONS = ['день', 'неделя', 'месяц'] as const;
const WEEKDAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const; // 1–7 = Пн–Вс

/** Соответствие кнопкам Пн–Вс: 1=Пн … 7=Вс (getDay(): 0=Вс → 7). */
function getLocalWeekdayOneToSeven(): number {
  const d = new Date().getDay();
  return d === 0 ? 7 : d;
}

export type RecurrenceState = {
  interval: number;
  unit: 'day' | 'week' | 'month';
  weekdays: number[];
  /** День месяца для unit === 'month' (1–31). */
  monthDay?: number;
  endType: 'never' | 'until_date' | 'after_count';
  endDate?: string;
  endCount?: number;
};

const getDefaultRecurrence = (): RecurrenceState => ({
  interval: 1,
  unit: 'week',
  weekdays: [getLocalWeekdayOneToSeven()],
  monthDay: 1,
  endType: 'never',
  endDate: undefined,
  endCount: 12,
});

const unitToOption: Record<RecurrenceState['unit'], string> = {
  day: 'день',
  week: 'неделя',
  month: 'месяц',
};
const optionToUnit = { день: 'day' as const, неделя: 'week' as const, месяц: 'month' as const };

type RecurrenceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialRecurrence?: RecurrenceState | null;
  onDone: (recurrence: RecurrenceState) => void;
};

export const RecurrenceModal: FC<RecurrenceModalProps> = ({ isOpen, onClose, initialRecurrence, onDone }) => {
  const [state, setState] = useState<RecurrenceState>(() => initialRecurrence ?? getDefaultRecurrence());

  useEffect(() => {
    if (isOpen) {
      setState(initialRecurrence ?? getDefaultRecurrence());
    }
  }, [isOpen, initialRecurrence]);

  const setIntervalVal = (value: number) => {
    const n = Math.max(1, Math.min(99, value));
    setState((s) => ({ ...s, interval: n }));
  };

  const setMonthDayVal = (value: number) => {
    const n = Math.max(1, Math.min(31, value));
    setState((s) => ({ ...s, monthDay: n }));
  };

  const toggleWeekday = (day: number) => {
    setState((s) => {
      const next = s.weekdays.includes(day)
        ? s.weekdays.filter((d) => d !== day)
        : [...s.weekdays, day].sort((a, b) => a - b);
      return { ...s, weekdays: next };
    });
  };

  const handleDone = () => {
    onDone(state);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalWrap isShow={isOpen}>
      <div className="flex flex-col gap-6 w-full max-w-[600px] rounded-[20px] p-6 sm:p-8 bg-white shadow-lg">
        <div className="w-full flex justify-between items-center">
          <h2 className="font-comfortaa text-xl sm:text-2xl text-[#111026] font-bold">Повторение встречи</h2>
          <button type="button" className="cursor-pointer p-1" onClick={onClose} aria-label="Закрыть">
            <CloseCrossBtn width={20} height={20} />
          </button>
        </div>

        {/* Повторять с интервалом */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3 flex-nowrap">
            <p className="text-[#2C3038] text-base font-semibold font-manrope whitespace-nowrap shrink-0">
              Повторять с интервалом
            </p>
            <div className="flex items-center border border-greyBase rounded-[10px] overflow-hidden bg-white shrink-0">
              <Input
                type="number"
                min={1}
                max={99}
                value={state.interval}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') return;
                  const n = parseInt(v, 10);
                  if (!Number.isNaN(n)) setIntervalVal(n);
                }}
                className="!h-[40px] !w-[88px] !min-w-[88px] text-center hide-input-number-arrow border-0 rounded-none"
                classNameWrap="!h-[40px] [&>div]:!h-[40px]"
              />
              <div className="flex flex-col border-l border-greyBase w-9 shrink-0">
                <button
                  type="button"
                  className="w-9 h-[20px] flex items-center justify-center text-[#585858] hover:bg-greyBase/20 border-b border-greyBase/50"
                  onClick={() => setIntervalVal(state.interval + 1)}
                  aria-label="Увеличить"
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M2 8L6 4L10 8" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="w-9 h-[20px] flex items-center justify-center text-[#585858] hover:bg-greyBase/20"
                  onClick={() => setIntervalVal(state.interval - 1)}
                  aria-label="Уменьшить"
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M2 4L6 8L10 4" />
                  </svg>
                </button>
              </div>
            </div>
            <CustomSelect
              selected={unitToOption[state.unit]}
              items={[...INTERVAL_UNIT_OPTIONS]}
              onChange={(val) => setState((s) => ({ ...s, unit: optionToUnit[val as keyof typeof optionToUnit] }))}
              showError={false}
              classNameInput="!h-[40px] !min-h-[40px] md:p-[13px_20px] !text-[16px]"
              classNameWrap="[&>div.relative]:!h-[40px] w-[140px]"
            />
          </div>

          {/* День месяца (только для месяца) */}
          {state.unit === 'month' && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3 flex-nowrap">
                <p className="text-[#2C3038] text-base font-semibold font-manrope whitespace-nowrap shrink-0">
                  День месяца
                </p>
                <div className="flex items-center border border-greyBase rounded-[10px] overflow-hidden bg-white shrink-0">
                  <Input
                    type="number"
                    min={1}
                    max={31}
                    value={state.monthDay ?? 1}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '') return;
                      const n = parseInt(v, 10);
                      if (!Number.isNaN(n)) setMonthDayVal(n);
                    }}
                    className="!h-[40px] !w-[72px] !min-w-[72px] text-center hide-input-number-arrow border-0 rounded-none"
                    classNameWrap="!h-[40px] [&>div]:!h-[40px]"
                  />
                  <div className="flex flex-col border-l border-greyBase w-9 shrink-0">
                    <button
                      type="button"
                      className="w-9 h-[20px] flex items-center justify-center text-[#585858] hover:bg-greyBase/20 border-b border-greyBase/50"
                      onClick={() => setMonthDayVal((state.monthDay ?? 1) + 1)}
                      aria-label="Увеличить"
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M2 8L6 4L10 8" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="w-9 h-[20px] flex items-center justify-center text-[#585858] hover:bg-greyBase/20"
                      onClick={() => setMonthDayVal((state.monthDay ?? 1) - 1)}
                      aria-label="Уменьшить"
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M2 4L6 8L10 4" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              {(state.monthDay ?? 1) === 31 && (
                <p
                  className="font-normal text-[12px] text-[#FF383C]"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400 }}
                >
                  Встреча добавится в каждый последний день месяца.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Дни повторения (только для недели) */}
        {state.unit === 'week' && (
          <div className="flex flex-col gap-2">
            <p className="text-[#2C3038] text-base font-semibold font-manrope">Дни повторения</p>
            <div className="inline-flex w-[490px] max-w-full border border-[#E5E5E5] rounded-[20px] overflow-hidden">
              {(WEEKDAY_LABELS as unknown as string[]).map((label, index) => {
                const day = index + 1;
                const isSelected = state.weekdays.includes(day);
                const isFirst = index === 0;
                const isLast = index === 6;
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleWeekday(day)}
                    className={classNames(
                      'h-[40px] min-w-[70px] w-[70px] flex items-center justify-center transition-colors font-manrope text-base font-medium relative flex-shrink-0 cursor-pointer',
                      isFirst && 'rounded-l-[20px]',
                      isLast && 'rounded-r-[20px]',
                      isSelected ? 'bg-redBase text-white' : 'bg-white text-[#737478]',
                      index < 6 && 'border-r border-[#E5E5E5]',
                    )}
                    style={{
                      ...(isFirst && { borderTopLeftRadius: '20px', borderBottomLeftRadius: '20px' }),
                      ...(isLast && { borderTopRightRadius: '20px', borderBottomRightRadius: '20px' }),
                    }}
                  >
                    <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                      {label}
                    </span>
                    {isSelected && (
                      <svg
                        className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 flex-shrink-0 pointer-events-none"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Окончание */}
        <div className="flex flex-col gap-3">
          <p className="text-[#2C3038] text-base font-semibold font-manrope">Окончание</p>
          <div className="flex flex-col gap-3">
            <RadioOption
              name="recurrence-end"
              checked={state.endType === 'never'}
              onChange={() => setState((s) => ({ ...s, endType: 'never' }))}
              label="Никогда"
            />
            <RadioOption
              name="recurrence-end"
              checked={state.endType === 'until_date'}
              onChange={() =>
                setState((s) => ({
                  ...s,
                  endType: 'until_date',
                  endDate: s.endDate || dayjs().add(1, 'month').format('YYYY-MM-DD'),
                }))
              }
              label="Дата"
              labelWrap
            >
              {state.endType === 'until_date' && (
                <div className="w-full sm:w-auto">
                  <DatePickerCustom
                    date={state.endDate || dayjs().add(1, 'month').format('YYYY-MM-DD')}
                    setDate={(d) => setState((s) => ({ ...s, endDate: d }))}
                    className="!h-[40px] [&>input]:!text-[16px]"
                  />
                </div>
              )}
            </RadioOption>
            <RadioOption
              name="recurrence-end"
              checked={state.endType === 'after_count'}
              onChange={() => setState((s) => ({ ...s, endType: 'after_count' }))}
              label="После"
              labelWrap
            >
              {state.endType === 'after_count' && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center border border-greyBase rounded-[10px] overflow-hidden bg-white shrink-0">
                    <Input
                      type="number"
                      min={1}
                      max={99}
                      value={state.endCount ?? 12}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') {
                          setState((s) => ({ ...s, endCount: undefined }));
                          return;
                        }
                        const n = parseInt(v, 10);
                        if (!Number.isNaN(n) && n >= 1) setState((s) => ({ ...s, endCount: n }));
                      }}
                      className="!h-[40px] !w-[72px] !min-w-[72px] text-center hide-input-number-arrow border-0 rounded-none"
                      classNameWrap="!h-[40px] [&>div]:!h-[40px]"
                    />
                    <div className="flex flex-col border-l border-greyBase w-9 shrink-0">
                      <button
                        type="button"
                        className="w-9 h-[20px] flex items-center justify-center text-[#585858] hover:bg-greyBase/20 border-b border-greyBase/50"
                        onClick={() =>
                          setState((s) => ({
                            ...s,
                            endCount: Math.min(99, (s.endCount ?? 12) + 1),
                          }))
                        }
                        aria-label="Увеличить"
                      >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M2 8L6 4L10 8" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="w-9 h-[20px] flex items-center justify-center text-[#585858] hover:bg-greyBase/20"
                        onClick={() =>
                          setState((s) => ({
                            ...s,
                            endCount: Math.max(1, (s.endCount ?? 12) - 1),
                          }))
                        }
                        aria-label="Уменьшить"
                      >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M2 4L6 8L10 4" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <span className="font-manrope text-base text-[#585858]">повторов</span>
                </div>
              )}
            </RadioOption>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button
            type="grey"
            onClick={onClose}
            className="font-manrope font-normal !text-base !text-[#2C3038] !py-[15px] !px-[40px] !min-w-0 !w-max !rounded-[16px] !bg-[#ECEFFA] hover:!bg-[#dfe3f0] !border-0"
          >
            Отменить
          </Button>
          <Button
            type="black"
            onClick={handleDone}
            className="font-manrope font-normal !text-base !text-[#FFFFFF] !py-[15px] !px-[40px] !min-w-0 !w-max !rounded-[16px] !bg-[#111026] hover:!bg-[#1a1d35] !border-0"
          >
            Готово
          </Button>
        </div>
      </div>
    </ModalWrap>
  );
};
